import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  ArrowRight,
  Shield,
  Lock,
  Eye,
  Server,
  Key,
  UserCheck,
  FileCheck,
  Globe,
  CheckCircle,
  AlertTriangle,
  CreditCard,
  Mail,
  Database,
  RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";

export default function DataSecurityPage() {
  const securityFeatures = [
    {
      icon: Lock,
      title: "256-bit SSL Encryption",
      description: "All data transmitted between your browser and our servers is encrypted using industry-standard SSL/TLS encryption. This is the same level of security used by banks.",
      badge: "Industry Standard"
    },
    {
      icon: Database,
      title: "Secure Data Storage",
      description: "Your personal information is stored in encrypted databases with multiple layers of protection. We use MongoDB Atlas with enterprise-grade security features.",
      badge: "Enterprise Grade"
    },
    {
      icon: Key,
      title: "Password Protection",
      description: "Passwords are never stored in plain text. We use bcrypt hashing with salting, making it virtually impossible for anyone (including us) to see your password.",
      badge: "Bcrypt Hashed"
    },
    {
      icon: UserCheck,
      title: "Secure Authentication",
      description: "We use JWT (JSON Web Tokens) for session management with automatic expiration. Optional Google OAuth provides an additional secure login method.",
      badge: "JWT Protected"
    },
    {
      icon: Server,
      title: "Protected Infrastructure",
      description: "Our servers are hosted in secure data centers with 24/7 monitoring, automatic backups, and DDoS protection to ensure your data is always safe.",
      badge: "24/7 Monitored"
    },
    {
      icon: RefreshCw,
      title: "Regular Security Audits",
      description: "We continuously monitor our systems for vulnerabilities and implement security updates promptly. Our code undergoes regular security reviews.",
      badge: "Continuously Updated"
    }
  ];

  const dataHandling = [
    {
      category: "What We Collect",
      items: [
        "Email addresses (for task delivery)",
        "Name and country (for account verification)",
        "Payment information (PayPal email only)",
        "Social media profile (for identity verification)",
        "Task completion data (for rewards tracking)"
      ]
    },
    {
      category: "What We DON'T Collect",
      items: [
        "Credit card numbers or bank details",
        "Social security or government IDs",
        "Passwords in readable format",
        "Location tracking or GPS data",
        "Personal photos or documents"
      ]
    },
    {
      category: "How We Use Your Data",
      items: [
        "Deliver task emails to your registered addresses",
        "Process your withdrawal payments",
        "Verify your identity to prevent fraud",
        "Improve our services and user experience",
        "Communicate important account updates"
      ]
    }
  ];

  const yourRights = [
    {
      icon: Eye,
      title: "Access Your Data",
      description: "Download a complete copy of all personal data we store about you at any time from your Profile page."
    },
    {
      icon: FileCheck,
      title: "Correct Your Data",
      description: "Update or correct any inaccurate information in your profile settings whenever needed."
    },
    {
      icon: AlertTriangle,
      title: "Delete Your Data",
      description: "Request complete deletion of your account and all associated data through the Profile page."
    },
    {
      icon: Mail,
      title: "Control Communications",
      description: "Manage your notification preferences and opt out of non-essential communications."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <Link to="/register">
            <Button className="bg-[#00B894] hover:bg-[#00A085] text-white">
              Join Securely
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-white via-[#00B894]/5 to-[#0984E3]/5">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#00B894]/10 text-[#00B894] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Shield className="w-4 h-4" />
            Your security is our priority
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            How We Keep Your{" "}
            <span className="bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent">
              Data Safe
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            We take the protection of your personal information seriously. Learn about the 
            measures we have in place to keep your data secure and your privacy protected.
          </p>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="py-8 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            <div>
              <Lock className="w-8 h-8 mx-auto mb-2 text-[#00B894]" />
              <p className="text-sm text-slate-300">SSL Encrypted</p>
            </div>
            <div>
              <Shield className="w-8 h-8 mx-auto mb-2 text-[#00B894]" />
              <p className="text-sm text-slate-300">GDPR Compliant</p>
            </div>
            <div>
              <CreditCard className="w-8 h-8 mx-auto mb-2 text-[#00B894]" />
              <p className="text-sm text-slate-300">Secure Payments</p>
            </div>
            <div>
              <Globe className="w-8 h-8 mx-auto mb-2 text-[#00B894]" />
              <p className="text-sm text-slate-300">Global Standards</p>
            </div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Security Measures We Use
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Multiple layers of protection work together to keep your information safe
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-6 hover:bg-slate-100 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#00B894]/10 rounded-xl flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-[#00B894]" />
                  </div>
                  <span className="text-xs font-medium bg-[#00B894]/10 text-[#00B894] px-2 py-1 rounded-full">
                    {feature.badge}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Handling */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Transparent Data Handling
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We believe you should know exactly what data we collect and how we use it
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {dataHandling.map((section, index) => (
              <div key={index} className={`bg-white rounded-2xl p-6 shadow-sm ${
                index === 1 ? 'border-2 border-red-200' : ''
              }`}>
                <h3 className={`text-lg font-semibold mb-4 ${
                  index === 1 ? 'text-red-700' : 'text-slate-900'
                }`}>
                  {section.category}
                </h3>
                <ul className="space-y-3">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-2 text-sm">
                      <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                        index === 1 ? 'text-red-500' : 'text-[#00B894]'
                      }`} />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Your Rights */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Your Privacy Rights
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              You have full control over your personal data. Here's what you can do:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {yourRights.map((right, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-[#00B894]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <right.icon className="w-8 h-8 text-[#00B894]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{right.title}</h3>
                <p className="text-slate-600 text-sm">{right.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center">
            <p className="text-blue-800 mb-4">
              To exercise any of these rights, visit your <strong>Profile</strong> page or contact us at{" "}
              <a href="mailto:privacy@gigseasy.com" className="text-[#00B894] hover:underline">
                privacy@gigseasy.com
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Payment Security */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto text-center text-white">
          <CreditCard className="w-16 h-16 mx-auto mb-6 text-[#00B894]" />
          <h2 className="text-3xl font-bold mb-4">
            Payment Security
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Your financial information is handled with the utmost care. We use PayPal for all 
            payments, which means we never see or store your bank details or credit card numbers.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/5 rounded-xl p-6">
              <CheckCircle className="w-8 h-8 text-[#00B894] mx-auto mb-3" />
              <p className="text-sm text-slate-300">We only store your PayPal email</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6">
              <CheckCircle className="w-8 h-8 text-[#00B894] mx-auto mb-3" />
              <p className="text-sm text-slate-300">PayPal handles all payment processing</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6">
              <CheckCircle className="w-8 h-8 text-[#00B894] mx-auto mb-3" />
              <p className="text-sm text-slate-300">No credit card info stored on our servers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="w-16 h-16 text-[#00B894] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Our Commitment to You
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            We will never sell your personal data to third parties. We will always be 
            transparent about how we use your information. And we will continuously 
            improve our security measures to protect you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/privacy-policy">
              <Button size="lg" variant="outline" className="border-slate-300">
                Read Privacy Policy
              </Button>
            </Link>
            <Link to="/register">
              <Button size="lg" className="bg-[#00B894] hover:bg-[#00A085] text-white px-8">
                Join GigsEasy Securely
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
